import React, { useState, useEffect, useRef } from 'react';

const VoiceRecorder = ({
  onRecordingComplete,
  onError,
  isEnabled = true,
  onToggleEnable
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);

  const audioRef = useRef(null);
  const recordingTimerRef = useRef(null);

  // Voice recording timer effect
  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }

    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [isRecording]);

  // Notify parent when recording is complete
  useEffect(() => {
    if (audioBlob) {
      onRecordingComplete(audioBlob, audioUrl);
    }
  }, [audioBlob, audioUrl, onRecordingComplete]);

  const resetVoiceRecording = () => {
    setIsRecording(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);

    if (mediaRecorder) {
      const { processor, audioContext, stream, stopRecordingActive } = mediaRecorder;

      // Stop recording first
      if (stopRecordingActive) {
        stopRecordingActive();
      }

      // Clean up resources
      if (processor) {
        processor.disconnect();
      }

      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close();
      }

      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }

    setMediaRecorder(null);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 44100,
          channelCount: 1,
          volume: 1.0
        }
      });

      // FIX 1: Create AudioContext without specifying sample rate
      // Let it use the device's native sample rate
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Get the actual sample rate being used
      const actualSampleRate = audioContext.sampleRate;
      const source = audioContext.createMediaStreamSource(stream);

      // Create script processor for capturing audio data
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      const recordedChunks = [];
      let isRecordingActive = true;

      processor.onaudioprocess = (e) => {
        if (!isRecordingActive) return;

        const inputData = e.inputBuffer.getChannelData(0);
        // Copy the data (important because the buffer gets reused)
        const chunk = new Float32Array(inputData.length);
        chunk.set(inputData);
        recordedChunks.push(chunk);
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      // Store references for cleanup (include actualSampleRate)
      setMediaRecorder({
        processor,
        audioContext,
        stream,
        recordedChunks,
        source,
        actualSampleRate, // Store the actual sample rate
        isRecordingActive: () => isRecordingActive,
        stopRecordingActive: () => { isRecordingActive = false; }
      });
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      console.error("Error starting recording:", error);
      onError("Unable to access microphone. Please check your permissions.");
    }
  };

  // Helper functions for WAV file creation
  const floatTo16BitPCM = (output, offset, input) => {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const createWavFile = (audioData, sampleRate) => {
    const numChannels = 1; // Mono
    const bitsPerSample = 16;
    const bytesPerSample = bitsPerSample / 8;
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = audioData.length * bytesPerSample;

    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    // WAV file header (44 bytes)
    writeString(view, 0, 'RIFF');                    // ChunkID
    view.setUint32(4, 36 + dataSize, true);         // ChunkSize
    writeString(view, 8, 'WAVE');                   // Format
    writeString(view, 12, 'fmt ');                  // Subchunk1ID
    view.setUint32(16, 16, true);                   // Subchunk1Size (16 for PCM)
    view.setUint16(20, 1, true);                    // AudioFormat (1 for PCM)
    view.setUint16(22, numChannels, true);          // NumChannels
    view.setUint32(24, sampleRate, true);           // SampleRate
    view.setUint32(28, byteRate, true);             // ByteRate
    view.setUint16(32, blockAlign, true);           // BlockAlign
    view.setUint16(34, bitsPerSample, true);        // BitsPerSample
    writeString(view, 36, 'data');                  // Subchunk2ID
    view.setUint32(40, dataSize, true);             // Subchunk2Size

    // Convert float samples to 16-bit PCM
    floatTo16BitPCM(view, 44, audioData);

    return new Blob([buffer], { type: 'audio/wav' });
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      setIsRecording(false);

      const { processor, audioContext, stream, recordedChunks, actualSampleRate, stopRecordingActive } = mediaRecorder;

      // Stop the recording flag first
      if (stopRecordingActive) {
        stopRecordingActive();
      }

      // Wait a bit for any pending audio processing
      setTimeout(() => {
        // Stop the processor and clean up
        if (processor) {
          processor.disconnect();
        }

        if (audioContext) {
          audioContext.close();
        }

        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }

        // Combine all recorded chunks
        let totalLength = 0;
        recordedChunks.forEach(chunk => {
          totalLength += chunk.length;
        });

        if (totalLength > 0) {
          const combinedAudio = new Float32Array(totalLength);
          let offset = 0;
          recordedChunks.forEach(chunk => {
            combinedAudio.set(chunk, offset);
            offset += chunk.length;
          });

          // FIX 2: Use the actual sample rate from the AudioContext
          const wavBlob = createWavFile(combinedAudio, actualSampleRate);
          const audioUrl = URL.createObjectURL(wavBlob);

          setAudioBlob(wavBlob);
          setAudioUrl(audioUrl);
        } else {
          onError("Recording failed - no audio data captured");
        }

        setMediaRecorder(null);
      }, 100);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="voice-recording-block">
      <div className="voice-recording-content" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div className="voice-morphing-toggle mb-4">
          <div className="form-check form-switch d-flex justify-content-center align-items-center">
            <input
              className="form-check-input me-3"
              type="checkbox"
              id="voiceMorphingToggle"
              checked={isEnabled}
              onChange={(e) => onToggleEnable(e.target.checked)}
              style={{ transform: 'scale(1.5)' }}
            />
            <label className="form-check-label fs-5" htmlFor="voiceMorphingToggle">
              <strong>Voice Morphing</strong> <span className="badge bg-warning text-dark ms-2">Premium</span>
            </label>
          </div>
          <p className="text-muted mt-2">
            {isEnabled
              ? "Record your voice to create personalized speech animations"
              : "Skip this step to use default voice synthesis"
            }
          </p>
        </div>

        {isEnabled && (
          <>
            {!audioBlob ? (
              <div className="recording-controls">
                <div className="recording-status mb-4">
                  {isRecording ? (
                    <div>
                      <div className="recording-indicator d-flex justify-content-center align-items-center mb-3">
                        <div className="recording-pulse me-2"></div>
                        <span className="fs-4 text-danger">Recording: {formatTime(recordingTime)}</span>
                      </div>
                      <p className="text-muted">Speak clearly for 5-10 seconds</p>
                    </div>
                  ) : (
                    <div>
                      <i className="fa fa-microphone fs-1 text-primary mb-3"></i>
                      <p className="fs-5">Ready to record your voice</p>
                    </div>
                  )}
                </div>

                <div className="recording-buttons">
                  {!isRecording ? (
                    <button
                      type="button"
                      className="btn btn-primary btn-lg px-5"
                      onClick={startRecording}
                    >
                      <i className="fa fa-microphone me-2"></i>
                      Start Recording
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-danger btn-lg px-5"
                      onClick={stopRecording}
                    >
                      <i className="fa fa-stop me-2"></i>
                      Stop Recording
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="recording-playback">
                <div className="playback-controls mb-4">
                  <i className="fa fa-check-circle fs-1 text-success mb-3"></i>
                  <p className="fs-5">Voice recording completed!</p>
                  <audio controls className="w-100 mb-3" ref={audioRef}>
                    <source src={audioUrl} type="audio/wav" />
                  </audio>
                </div>

                <div className="playback-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary me-3"
                    onClick={resetVoiceRecording}
                  >
                    <i className="fa fa-redo me-2"></i>
                    Re-record
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* CSS for recording pulse animation */}
        <style jsx='true'>{`
          .recording-pulse {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background-color: #dc3545;
            animation: pulse 1.5s infinite;
          }

          @keyframes pulse {
            0% {
              transform: scale(0.95);
              box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
            }

            70% {
              transform: scale(1);
              box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
            }

            100% {
              transform: scale(0.95);
              box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
            }
          }

          .voice-recording-block {
            min-height: 400px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f8f9fa;
            border-radius: 10px;
            border: 2px dashed #dee2e6;
          }

          .form-check-input:checked {
            background-color: #28a745 !important;
            border-color: #28a745 !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default VoiceRecorder;
